import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { ChevronDown, ChevronRight, ExternalLink, FileSpreadsheet } from "lucide-react";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";

// DRE Gerencial: tela 100% somente leitura — nenhum valor é editável aqui, tudo vem
// agregado dos módulos de origem (ver backend/services/dre_gerencial_svc.py). Realizado e
// Aderência vêm sempre `null` do backend nesta fase (fonte externa ainda não conectada) —
// exibidos literalmente como "-", nunca como R$ 0,00, para não sugerir um valor real zerado.

const KPI_LABELS = {
  faturamento_bruto: "Faturamento Bruto",
  receita_liquida: "Receita Líquida",
  lucro_bruto: "Lucro Bruto",
  ebitda: "EBITDA",
  lair: "LAIR",
  resultado_liquido: "Resultado Líquido",
};

export function valorOuTraco(valor, unidade) {
  if (valor === null || valor === undefined) return "-";
  return unidade === "pct" ? formatPercent(valor) : formatCurrency(valor);
}

function KpiCard({ label, linha, C }) {
  const totalPlanejado = linha ? linha.total.planejado : 0;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", minWidth: 180, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{formatCurrency(totalPlanejado)}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Realizado: -</div>
    </div>
  );
}

export function thStyle(C, extra = {}) {
  return {
    padding: "6px 8px", fontSize: 10.5, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.03em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    ...extra,
  };
}

export function tdStyle(C, extra = {}) {
  return {
    padding: "6px 8px", fontSize: 11.5, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    ...extra,
  };
}

const BG_POR_TIPO = (C) => ({
  grupo: C.categoriaBg,
  subgrupo: C.subcategoriaBg,
  conta: "transparent",
  derivado: C.bgActive,
  margem: C.margemBg,
});

const PADDING_POR_NIVEL = [10, 16, 32, 48];

export function LinhaRow({ linha, nivel, colapsados, toggle, C, ano, comTrimestres }) {
  const navigate = useNavigate();
  const temFilhos = Array.isArray(linha.filhos) && linha.filhos.length > 0;
  const colapsada = colapsados.has(linha.id);
  const bg = BG_POR_TIPO(C)[linha.tipo] || "transparent";
  const negrito = linha.tipo === "grupo" || linha.tipo === "derivado" || linha.tipo === "margem";
  const italico = linha.tipo === "margem";

  return (
    <Fragment>
      <tr
        onClick={temFilhos ? () => toggle(linha.id) : undefined}
        style={{
          background: bg,
          fontWeight: negrito ? 700 : 400,
          fontStyle: italico ? "italic" : "normal",
          cursor: temFilhos ? "pointer" : "default",
          borderTop: linha.tipo === "derivado" ? `2px solid ${C.border}` : undefined,
        }}
      >
        <td style={tdStyle(C, { textAlign: "left" })}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: PADDING_POR_NIVEL[nivel] || 10 }}>
            {temFilhos ? (colapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />) : null}
            <span>{linha.label}</span>
            {linha.origem_modulo && (
              <button
                type="button"
                title="Ver lançamentos no módulo de origem"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/gestao-orcamentaria/${linha.origem_modulo}?ano=${ano}`);
                }}
                style={{
                  display: "inline-flex", alignItems: "center", background: "transparent",
                  border: "none", cursor: "pointer", color: C.muted, padding: 2,
                }}
              >
                <ExternalLink size={11} />
              </button>
            )}
          </div>
        </td>
        {linha.meses.map((m) => (
          <td key={m.mes} style={tdStyle(C)}>{valorOuTraco(m.planejado, linha.unidade)}</td>
        ))}
        {comTrimestres && (linha.trimestres || []).map((t, i) => (
          <td
            key={t.trimestre}
            style={tdStyle(C, { background: C.bgActive, borderLeft: i === 0 ? `2px solid ${C.brand}` : undefined })}
          >
            {valorOuTraco(t.planejado, linha.unidade)}
          </td>
        ))}
        <td style={tdStyle(C, { fontWeight: 700, background: comTrimestres ? C.bgActive : undefined })}>{valorOuTraco(linha.total.planejado, linha.unidade)}</td>
        <td style={tdStyle(C)}>{linha.aderencia === null || linha.aderencia === undefined ? "-" : formatPercent(linha.aderencia)}</td>
      </tr>

      {temFilhos && !colapsada && linha.filhos.map((filho) => (
        <LinhaRow key={filho.id} linha={filho} nivel={nivel + 1} colapsados={colapsados} toggle={toggle} C={C} ano={ano} comTrimestres={comTrimestres} />
      ))}
    </Fragment>
  );
}

export function coletarIdsComFilhos(linhas) {
  const ids = [];
  for (const linha of linhas || []) {
    if (Array.isArray(linha.filhos) && linha.filhos.length > 0) {
      ids.push(linha.id);
      ids.push(...coletarIdsComFilhos(linha.filhos));
    }
  }
  return ids;
}

// Estado inicial/padrão ao trocar de ano: categorias (grupo) abertas, subcategorias
// (subgrupo) fechadas — evita abrir a árvore inteira (200+ linhas) de uma vez.
export function coletarIdsSubgrupo(linhas) {
  const ids = [];
  for (const linha of linhas || []) {
    if (linha.tipo === "subgrupo") ids.push(linha.id);
    if (Array.isArray(linha.filhos)) ids.push(...coletarIdsSubgrupo(linha.filhos));
  }
  return ids;
}

// ── Exportar para Excel (mesma convenção de GestaoOrcamentariaView.js/investimentos.js: linha
// crua por conta da árvore, indentada por nível, um valor numérico por coluna — nunca formatado
// como moeda/percentual, para ficar aproveitável em outra planilha) ─────────────────────────

export function achatarLinhas(linhas, nivel = 0, saida = []) {
  for (const linha of linhas || []) {
    saida.push({ linha, nivel });
    if (Array.isArray(linha.filhos)) achatarLinhas(linha.filhos, nivel + 1, saida);
  }
  return saida;
}

function valorOuTracoExport(valor) {
  return valor === null || valor === undefined ? "-" : valor;
}

export function colunasMensais(linha) {
  const colunas = {};
  linha.meses.forEach((m, i) => {
    colunas[`${MESES_ABREV[i]} (Plan.)`] = m.planejado;
    colunas[`${MESES_ABREV[i]} (Real.)`] = valorOuTracoExport(m.realizado);
  });
  return colunas;
}

export function criarWorkbook() {
  return XLSX.utils.book_new();
}

export function adicionarAba(workbook, nomeAba, linhasPlanas) {
  const worksheet = XLSX.utils.json_to_sheet(linhasPlanas);
  XLSX.utils.book_append_sheet(workbook, worksheet, nomeAba.slice(0, 31));
}

export function baixarWorkbook(workbook, nomeArquivo) {
  XLSX.writeFile(workbook, `${nomeArquivo.replace(/[^\w]+/g, "_")}.xlsx`);
}

export default function DreGerencialView({ data, ano, C }) {
  const [colapsados, setColapsados] = useState(() => new Set(coletarIdsComFilhos(data?.linhas || [])));

  useEffect(() => {
    setColapsados(new Set(coletarIdsComFilhos(data?.linhas || [])));
  }, [data]);

  function toggle(id) {
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const kpis = data?.kpis || {};
  const linhas = useMemo(() => data?.linhas || [], [data]);
  const idsComFilhos = useMemo(() => coletarIdsComFilhos(linhas), [linhas]);

  function exportarParaExcel() {
    const linhasPlanas = achatarLinhas(linhas).map(({ linha, nivel }) => ({
      "Item": `${"  ".repeat(nivel)}${linha.label}`,
      ...colunasMensais(linha),
      "Total (Plan.)": linha.total.planejado,
      "Total (Real.)": linha.total.realizado === null || linha.total.realizado === undefined ? "-" : linha.total.realizado,
      "Aderência (%)": linha.aderencia === null || linha.aderencia === undefined ? "-" : linha.aderencia,
    }));
    const workbook = criarWorkbook();
    adicionarAba(workbook, "DRE Gerencial", linhasPlanas);
    baixarWorkbook(workbook, `DRE_Gerencial_${ano}`);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {Object.entries(KPI_LABELS).map(([chave, label]) => (
          <KpiCard key={chave} label={label} linha={kpis[chave]} C={C} />
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button
          type="button"
          onClick={exportarParaExcel}
          disabled={linhas.length === 0}
          title="Exportar dados para Excel"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "transparent", border: `1px solid ${C.success}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.success,
            cursor: linhas.length === 0 ? "not-allowed" : "pointer", opacity: linhas.length === 0 ? 0.45 : 1,
          }}
        >
          <FileSpreadsheet size={14} /> Exportar Excel
        </button>
        <button
          type="button"
          onClick={() => setColapsados(colapsados.size > 0 ? new Set() : new Set(idsComFilhos))}
          style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.text, cursor: "pointer",
          }}
        >
          {colapsados.size > 0 ? "Expandir tudo" : "Recolher tudo"}
        </button>
      </div>

      {/* Tabela */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>DRE Gerencial · {ano}</th>
                {MESES_ABREV.map((m) => (
                  <th key={m} style={thStyle(C, { textAlign: "center" })}>{m}</th>
                ))}
                <th style={thStyle(C, { textAlign: "center" })}>Total</th>
                <th style={thStyle(C)}>Aderência</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <LinhaRow key={linha.id} linha={linha} nivel={0} colapsados={colapsados} toggle={toggle} C={C} ano={ano} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
